import type { AbMetric, AbVariant } from '@emailforge/schemas';

import { apiSlice } from '@/features/api/apiSlice';

export type ExperimentRow = {
  id: string;
  templateId: string;
  name: string;
  metric: AbMetric;
  status: 'draft' | 'running' | 'finished' | 'cancelled';
  sampleSizeTarget: number;
  winnerVariantId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
};

export type VariantStats = {
  id: string;
  label: string;
  weight: number;
  assignments: number;
  opens: number;
  clicks: number;
  conversions: number;
  openRate: number;
  clickRate: number;
  conversionRate: number;
};

export type CredibleInterval = { lower: number; median: number; upper: number };

export type ExperimentDetail = ExperimentRow & {
  hypothesis: string | null;
  variants: AbVariant[];
  stats: VariantStats[];
  leader: VariantStats | null;
  bayesianProb: Record<string, number>;
  credibleIntervals: Record<string, CredibleInterval>;
  totalAssignments: number;
  progress: number;
};

export const experimentsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    listExperiments: b.query<ExperimentRow[], void>({
      query: () => '/experiments',
      providesTags: ['Experiment'],
    }),
    getExperiment: b.query<ExperimentDetail, string>({
      query: (id) => `/experiments/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Experiment', id }],
    }),
    createExperiment: b.mutation<
      { id: string },
      {
        templateId: string;
        name: string;
        hypothesis?: string;
        metric: AbMetric;
        variants: AbVariant[];
        sampleSizeTarget?: number;
        winnerAuto?: boolean;
      }
    >({
      query: (body) => ({ url: '/experiments', method: 'POST', body }),
      invalidatesTags: ['Experiment'],
    }),
    startExperiment: b.mutation<unknown, string>({
      query: (id) => ({ url: `/experiments/${id}/start`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Experiment', id }, 'Experiment'],
    }),
    cancelExperiment: b.mutation<unknown, string>({
      query: (id) => ({ url: `/experiments/${id}/cancel`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'Experiment', id }, 'Experiment'],
    }),
    declareWinner: b.mutation<unknown, { id: string; variantId?: string }>({
      query: ({ id, variantId }) => ({
        url: `/experiments/${id}/declare-winner`,
        method: 'POST',
        body: { variantId },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Experiment', id }, 'Experiment'],
    }),
    recordConversion: b.mutation<{ ok: true; matched: number }, { id: string; recipientHash: string }>({
      query: ({ id, recipientHash }) => ({
        url: `/experiments/${id}/conversions`,
        method: 'POST',
        body: { recipientHash },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Experiment', id }],
    }),
    conversionTimeSeries: b.query<Array<{ hour: string; variantId: string; conversions: number }>, string>({
      query: (id) => `/experiments/${id}/conversions/time-series`,
      providesTags: (_r, _e, id) => [{ type: 'Experiment', id }],
    }),
  }),
});

export const {
  useListExperimentsQuery,
  useGetExperimentQuery,
  useCreateExperimentMutation,
  useStartExperimentMutation,
  useCancelExperimentMutation,
  useDeclareWinnerMutation,
  useRecordConversionMutation,
  useConversionTimeSeriesQuery,
} = experimentsApi;
